import React from "react";
import { useTranslation } from "react-i18next";
import ScreenLayout from "../ui/ScreenLayout";
import Button from "../ui/Button";
import Icon from "../ui/Icon";
import { formatDecimal, formatEuros } from "../../utils/format";

// Anneau de la maquette : un arc vert tourne en continu autour de l'énergie
// rechargée, pour montrer que la recharge avance même quand la valeur bouge peu.
// Le masque se cale sur la taille de l'anneau (220 px mobile, 184 px web).
const RING_ARC_STYLE = {
  background:
    "conic-gradient(from 0deg, rgba(97,209,157,0) 0deg, #61D19D 200deg, #37966A 300deg, rgba(55,150,106,0) 360deg)",
  WebkitMask: "radial-gradient(circle closest-side, transparent calc(100% - 10px), #000 calc(100% - 9px))",
  mask: "radial-gradient(circle closest-side, transparent calc(100% - 10px), #000 calc(100% - 9px))",
};

const RING_GLOW_STYLE = {
  background: "radial-gradient(circle, rgba(97,209,157,.45) 0%, rgba(97,209,157,0) 70%)",
};

const StatCard = ({ label, children }) => (
  <div className="rounded-2xl border border-qv-line p-4">
    <div className="text-xs font-medium text-qv-muted">{label}</div>
    <div className="mt-1 text-[22px] font-bold">{children}</div>
  </div>
);

// Écran 1d : recharge en cours. En version web, l'anneau et les chiffres sont
// côte à côte.
export default function ChargingScreen({
  energyKwh,
  duration,
  pricePerKwh,
  email,
  onStop,
  isStopping,
  error,
  panel,
  children,
}) {
  const { t, i18n } = useTranslation();

  const footer = (
    <>
      {error && (
        <p className="text-sm text-center font-medium text-qv-red" role="alert">
          {error}
        </p>
      )}
      <Button variant="danger" onClick={onStop} loading={isStopping}>
        {isStopping ? t("startCharging.charging.stopping") : t("startCharging.charging.stop")}
      </Button>
    </>
  );

  return (
    <ScreenLayout footer={footer} panel={panel}>
      <div className="pt-2 md:pt-0">
        <span className="inline-flex items-center gap-2 rounded-full bg-qv-green-50 px-3.5 py-1.5 text-[13px] font-semibold text-qv-green">
          <span className="relative inline-block h-2 w-2" aria-hidden="true">
            <span className="absolute inset-0 rounded-full bg-qv-green animate-qv-ring" />
            <span className="absolute inset-0 rounded-full bg-qv-green" />
          </span>
          {t("startCharging.charging.status")}
        </span>

        <div className="md:mt-6 md:flex md:items-center md:gap-7">
          <div className="relative mx-auto mt-6 h-[220px] w-[220px] md:mx-0 md:mt-0 md:h-[184px] md:w-[184px] md:shrink-0">
            <div className="absolute -inset-3.5 rounded-full animate-qv-breathe md:-inset-3" style={RING_GLOW_STYLE} />
            <div className="absolute inset-0 rounded-full border-[10px] border-qv-green-50 md:border-[9px]" />
            <div className="absolute inset-0 rounded-full animate-qv-spin-slow" style={RING_ARC_STYLE} />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
              <span className="text-5xl leading-[52px] font-bold tracking-[-.03em] tabular-nums md:text-[42px] md:leading-[46px]">
                {formatDecimal(energyKwh, i18n.language)}
              </span>
              <span className="text-base font-semibold text-qv-muted md:text-sm">
                {t("startCharging.charging.energyUnit")}
              </span>
            </div>
          </div>

          <div
            className={`mt-5 grid gap-3 md:mt-0 md:flex-1 md:grid-cols-1 ${pricePerKwh ? "grid-cols-2" : "grid-cols-1"}`}
          >
            <StatCard label={t("startCharging.charging.duration")}>{duration}</StatCard>
            {pricePerKwh && (
              <StatCard label={t("startCharging.charging.pricePerKwh")}>
                {formatEuros(pricePerKwh, i18n.language)}
                <span className="text-sm font-semibold text-qv-muted">/kWh</span>
              </StatCard>
            )}
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-qv-brand-50 p-4 md:mt-6">
          <p className="mb-3 text-sm leading-5 font-bold">{t("startCharging.charging.canCloseTitle")}</p>
          <div className="flex flex-col gap-2.5 text-[13px] leading-[19px] font-medium">
            <div className="flex gap-2.5 items-start">
              <Icon name="letter-linear" size={20} className="text-qv-brand" />
              <span className="[overflow-wrap:anywhere]">
                {email
                  ? t("startCharging.charging.followLink", { email })
                  : t("startCharging.charging.followLinkNoEmail")}
              </span>
            </div>
            <div className="flex gap-2.5 items-start">
              <Icon name="qr-code-linear" size={20} className="text-qv-brand" />
              <span>{t("startCharging.charging.rescan")}</span>
            </div>
          </div>
        </div>

        {children}
      </div>
    </ScreenLayout>
  );
}
